import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface formItemState {
	type: string,
	val: string,
	valid: boolean,
	message: string
}
interface formUpdateState {
	form: string,
	type: string,
	id: string,
	val: string
}
interface formValidState {
	form: string,
	type: string,
	id: string,
	val: boolean
}
interface getFormValuesState {
	element: string,
	form: string
}
interface getPostcodeAddressState {
	id: string,
	postcode: string
}
export interface postcodeAddressState {
	premise: string,
	street: string,
	posttown: string,
	county: string,
	postcode: string
}
interface formState {
	data: { [key: string]: formItemState; }
}
interface formsState {
	forms: { [key: string]: formState; },
	addresses: { [key: string]: postcodeAddressState[] },
	isGettingAddress: { [key: string]: boolean }
}
interface setFormState {
	id: string,
	json: string
}
interface gettingAddressState {
	id: string,
	getting: boolean
}
interface copyFormState {
	from: string,
	to: string
}
const initialState: formsState = {
	forms: {},
	addresses: {},
	isGettingAddress: {}
}

const getRadioValue = (name: string): string => {
	const radioButtonGroup: NodeListOf<HTMLInputElement> = document.getElementsByName(name) as NodeListOf<HTMLInputElement>;
	const checkedRadio = Array.from(radioButtonGroup).find(
		(radio) => radio.checked
	);
	return (typeof checkedRadio !== 'undefined' ? checkedRadio.value : '');
}

export const getPostcodeAddresses = createAsyncThunk(
	'forms/getpostcodeaddresses',
	async (get: getPostcodeAddressState, _thunkAPI: any) => {
		try {
			let formdata = new FormData();
			formdata.append("id", get.id);
			formdata.append("postcode", get.postcode);
			formdata.append('identifier', 'web');

			return fetch(process.env.REACT_APP_API_BACKEND_URL + 'address-lookup.php', {
				method: 'POST',
				body: formdata
			})
				.then(async (response) => {
					return await response.text();
				})
				.catch((error) => {
					console.error(error);
				});
		} catch (error) {
			console.error(error);
		} finally {
		}
	}
);

export const formsSlice = createSlice({
	name: 'forms',
	initialState,
	reducers: {
		createFormWithId: (state, action: PayloadAction<string>) => {
			state.forms[action.payload] = { data: {} };
		},
		removeFormWithId: (state, action: PayloadAction<string>) => {
			delete state.forms[action.payload];
		},
		setFormValueWithId: (state, action: PayloadAction<formUpdateState>) => {
			if (!state.forms.hasOwnProperty(action.payload.form)) {
				state.forms[action.payload.form] = { data: {} };
			}
			if (!state.forms[action.payload.form].data.hasOwnProperty(action.payload.id)) {
				state.forms[action.payload.form].data[action.payload.id] = { type: action.payload.type, val: '', valid: true, message: '' };
			}
			state.forms[action.payload.form].data[action.payload.id].val = action.payload.val;
		},
		setFormValidWithId: (state, action: PayloadAction<formValidState>) => {
			if (!state.forms.hasOwnProperty(action.payload.form)) {
				state.forms[action.payload.form] = { data: {} };
			}
			if (!state.forms[action.payload.form].data.hasOwnProperty(action.payload.id)) {
				state.forms[action.payload.form].data[action.payload.id] = { type: action.payload.type, val: '', valid: true, message: '' };
			}
			state.forms[action.payload.form].data[action.payload.id].valid = action.payload.val;
		},
		setFormMessageWithId: (state, action: PayloadAction<formUpdateState>) => {
			if (!state.forms.hasOwnProperty(action.payload.form)) {
				state.forms[action.payload.form] = { data: {} };
			}
			if (!state.forms[action.payload.form].data.hasOwnProperty(action.payload.id)) {
				state.forms[action.payload.form].data[action.payload.id] = { type: action.payload.type, val: '', valid: true, message: '' };
			}
			state.forms[action.payload.form].data[action.payload.id].message = action.payload.val;
		},
		setFormValid: (state, action: PayloadAction<string>) => {
			if (!state.forms.hasOwnProperty(action.payload)) {
				return;
			}
			for (const p in state.forms[action.payload].data) {
				state.forms[action.payload].data[p].valid = true;
			}
		},
		getFormValues: (state, action: PayloadAction<getFormValuesState>) => {
			if (!state.forms.hasOwnProperty(action.payload.form)) {
				state.forms[action.payload.form] = { data: {} };
			}
			const form = document.getElementById(action.payload.element);
			if (form != null) {
				const inputs: NodeListOf<HTMLInputElement> = form.querySelectorAll("input");
				for (let i = 0; i < inputs.length; i++) {
					let element: HTMLInputElement = inputs[i];
					const value: string = (element.type === 'radio' ? getRadioValue(element.name) : (element.type === 'checkbox' ? element.checked.toString() : element.value));
					const fd: formItemState = {
						type: element.type,
						val: value,
						valid: true,
						message: ''
					}
					state.forms[action.payload.form].data[element.name] = fd;
				}
				const selects: NodeListOf<HTMLSelectElement> = form.querySelectorAll("select");
				for (let i = 0; i < selects.length; i++) {
					let element: HTMLSelectElement = selects[i];
					const fd: formItemState = {
						type: element.type,
						val: element.value,
						valid: true,
						message: ''
					}
					state.forms[action.payload.form].data[element.name] = fd;
				}
				if (action.payload.element === 'QF' && !state.forms[action.payload.form].data.hasOwnProperty('quote_developer_id')) {
					const fd: formItemState = {
						type: 'select-one',
						val: '',
						valid: true,
						message: ''
					}
					state.forms[action.payload.form].data['quote_developer_id'] = fd;
				}
			}
		},
		setFormValuesWithJSON: (state, action: PayloadAction<setFormState>) => {
			try {
				const json = JSON.parse(action.payload.json);
				if (!state.forms.hasOwnProperty(action.payload.id)) {
					state.forms[action.payload.id] = { data: {} };
				}
				for (const p in json) {
					if (!state.forms[action.payload.id].data.hasOwnProperty(p)) {
						state.forms[action.payload.id].data[p] = { type: json[p].type, val: '', valid: true, message: '' };
					}

					state.forms[action.payload.id].data[p].type = json[p].type;
					state.forms[action.payload.id].data[p].val = json[p].val;
					state.forms[action.payload.id].data[p].valid = true;
					state.forms[action.payload.id].data[p].message = '';
				}
			} catch { }
		},
		removePostcodeAddresses: (state, action: PayloadAction<string>) => {
			delete state.addresses[action.payload];
		},
		setGettingAddresses: (state, action: PayloadAction<gettingAddressState>) => {
			state.isGettingAddress[action.payload.id] = action.payload.getting;
		},
		copyForm: (state, action: PayloadAction<copyFormState>) => {
			if (!state.forms.hasOwnProperty(action.payload.to)) {
				state.forms[action.payload.to] = { data: {} };
			}
			for (const p in state.forms[action.payload.from].data) {
				state.forms[action.payload.to].data[p] = state.forms[action.payload.from].data[p];
			}
		}
	},
	extraReducers: (builder: { addCase: (arg0: any, arg1: { (state: any, action: any): void; (state: any, action: any): void; }) => void; }) => {
		builder.addCase(getPostcodeAddresses.fulfilled, (state: formsState, action: { payload: string; }) => {
			try {
				const json = JSON.parse(action.payload as string);
				const id = json.id;
				const addresses: postcodeAddressState[] = [];
				for (const a of json.addresses) {
					const address: postcodeAddressState = {
						premise: a.premise,
						street: a.street,
						posttown: a.posttown,
						county: a.county,
						postcode: a.postcode
					}
					addresses.push(address);
				}
				state.addresses[id] = addresses;
				state.isGettingAddress[id] = false;
			} catch (e) { }
		})
	}
})

export const { createFormWithId, removeFormWithId, setFormValueWithId, setFormValidWithId, setFormMessageWithId, setFormValid, getFormValues, setFormValuesWithJSON, removePostcodeAddresses, setGettingAddresses, copyForm } = formsSlice.actions;
export default formsSlice.reducer;